// src/services/inscricaoTorneioService.ts
import { apolloClient } from "../apollo/client";
import { gql } from '@apollo/client';
import axios from 'axios';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchLogout } from '../context/AuthContext';
import { StatusInscricao } from '../types/inscricao';

export { StatusInscricao };

export const INSCRICOES_ATLETA_TORNEIO = gql`
  query InscricoesPorAtletaTorneio($atletaId: ID!, $torneioId: ID!) {
    inscricoesTorneioPorAtletaIdTorneioId(atletaId: $atletaId, torneioId: $torneioId) {
      id
      dataInscricao
      observacao
      status
      compartilharInformacoes
      dataCancelamento
      motivoCancelamento
      equipe {
        id
        nome
        cidade
        atletas {
          id
          usuario {
            id
            nome
            imagemUrl
          }
        }
      }
      categoriaTorneio {
        id
        nome
        precoInscricao
        aplicarDescontoTorneio
        categoriaLiga {
          id
          nome
        }
      }
      pagamentoInscricao {
        id
        status
        valor
      }
    }
  }
`;

export interface InscricaoTorneio {
  id: string;
  dataInscricao?: string | null;
  observacao?: string | null;
  status: string;
  compartilharInformacoes: boolean;
  dataCancelamento?: string | null;
  motivoCancelamento?: string | null;
  equipe: {
    id: string;
    nome: string;
    cidade?: string | null;
    atletas: {
      id: string;
      usuario: { id: string; nome: string; imagemUrl?: string | null };
    }[];
  };
  categoriaTorneio?: {
    id: string;
    nome: string;
    precoInscricao?: number | null;
    aplicarDescontoTorneio?: boolean | null;
  } | null;
  pagamentoInscricao?: {
    id: string;
    status: string;
    valor?: number | null;
  } | null;
}


export interface InscricoesAtletaTorneioData {
  inscricoesTorneioPorAtletaIdTorneioId: InscricaoTorneio[];
}

export interface InscricoesAtletaTorneioVars {
  atletaId: string;
  torneioId: string;
}

// --------------------------------------------------------------------------------
// Query paginada: torneios com inscricao confirmada do atleta
// --------------------------------------------------------------------------------

export interface InscricaoTorneioResumo {
  id: string;
  dataInscricao?: string | null;
  status: string;
  equipe: {
    id: string;
    nome: string;
  };
  categoriaTorneio: {
    id: string;
    nome: string;
    torneio: {
      id: string;
      nome: string;
      avatarUrl?: string | null;
      bannerUrl?: string | null;
      username?: string | null;
      dataInicio?: string | null;
      dataFim?: string | null;
      local?: {
        id: string;
        nome: string;
        endereco?: {
          id: string;
          cidade: {
            id: string;
            nome: string;
            estado: {
              id: string;
              uf: string;
            };
          };
        } | null;
      } | null;
    };
  } | null;
}

export const INSCRICOES_TORNEIO_POR_ATLETA = gql`
  query InscricoesTorneioPorAtletaId($atletaId: ID!, $page: Int!, $size: Int!) {
    inscricoesTorneioPorAtletaId(
      atletaId: $atletaId
      page: $page
      size: $size
      sortBy: "dataInscricao"
      direction: "DESC"
    ) {
      page
      size
      totalItems
      totalPages
      items {
        id
        dataInscricao
        status
        equipe {
          id
          nome
        }
        categoriaTorneio {
          id
          nome
          torneio {
            id
            nome
            avatarUrl
            bannerUrl
            username
            dataInicio
            dataFim
            local {
              id
              nome
              endereco {
                id
                cidade {
                  id
                  nome
                  estado {
                    id
                    uf
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export interface InscricoesTorneioPorAtletaData {
  inscricoesTorneioPorAtletaId: {
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
    items: InscricaoTorneioResumo[];
  };
}

export interface InscricoesTorneioPorAtletaVars {
  atletaId: string;
  page: number;
  size: number;
}

export const listarInscricoesTorneioPorAtleta = async (
  atletaId: string,
  page: number,
  size: number
) => {
  const { data } = await apolloClient.query<
    InscricoesTorneioPorAtletaData,
    InscricoesTorneioPorAtletaVars
  >({
    query: INSCRICOES_TORNEIO_POR_ATLETA,
    variables: { atletaId, page, size },
    fetchPolicy: 'no-cache',
  });
  return data.inscricoesTorneioPorAtletaId;
};

export const CRIAR_INSCRICAO_TORNEIO = gql`
  mutation CriarInscricaoTorneio($input: InscricaoTorneioInput!) {
    criarInscricaoTorneio(input: $input) {
      id
      equipe {
        id
        nome
        cidade
        atletas {
          id
          usuario {
            id
            nome
            imagemUrl
            email
          }
        }
      }
      status
      compartilharInformacoes
      observacao
      dataInscricao
      dataCancelamento
      motivoCancelamento
      categoriaTorneio {
        id
        descricao
        nome
        numeroAtletas
        precoInscricao
        aplicarDescontoTorneio
        torneio {
          id
          nome
        }
      }
      pagamentoInscricao {
        id
        status
        valor
      }
    }
  }
`;

export interface CriarInscricaoTorneioInput {
  categoriaTorneioId: string;
  dataInscricao?: string;
  equipe: {
    nome: string;
    cidade: string;
    atletas: { id: string }[];
  };
  observacao?: string | null;
  status?: string;
}

export interface CriarInscricaoTorneioResponse {
  id: string;
  compartilharInformacoes: boolean;
  equipe: {
    id: string;
    nome: string;
    cidade?: string | null;
    atletas: {
      id: string;
      usuario: {
        id: string;
        nome: string;
        imagemUrl?: string | null;
        email: string;
      };
    }[];
  };
  status: string;
  observacao?: string | null;
  dataInscricao?: string | null;
  dataCancelamento?: string | null;
  motivoCancelamento?: string | null;
  categoriaTorneio: {
    id: string;
    descricao?: string | null;
    nome: string;
    numeroAtletas?: number | null;
    precoInscricao?: number | null;
    aplicarDescontoTorneio?: boolean | null;
    torneio: { id: string; nome: string };
  };
  pagamentoInscricao?: {
    id: string;
    status: string;
    valor?: number | null;
  } | null;
}

export interface CriarInscricaoTorneioData {
  criarInscricaoTorneio: CriarInscricaoTorneioResponse;
}

export interface CriarInscricaoTorneioVars {
  input: CriarInscricaoTorneioInput;
}

/**
 * Erro tipado lançado pelo `criarInscricaoTorneio` quando o backend retorna
 * uma exceção GraphQL com `extensions.code` (Phase 2: `InscricaoNaoPermitidaException`
 * com código estruturado, ex.: `CATEGORIA_SEM_VAGAS_FILA_DESLIGADA`).
 */
export class InscricaoMutationError extends Error {
  public readonly codigo?: string;
  constructor(message: string, codigo?: string) {
    super(message);
    this.name = 'InscricaoMutationError';
    this.codigo = codigo;
  }
}

export const criarInscricaoTorneio = async (
  input: CriarInscricaoTorneioInput
) => {
  try {
    const { data } = await apolloClient.mutate<
      CriarInscricaoTorneioData,
      CriarInscricaoTorneioVars
    >({
      mutation: CRIAR_INSCRICAO_TORNEIO,
      variables: { input },
    });
    return data?.criarInscricaoTorneio;
  } catch (err: any) {
    // Propaga código estruturado vindo do backend (extensions.code)
    // para o caller poder decidir mensagem específica vs. genérica.
    const gqlError = err?.graphQLErrors?.[0];
    const codigo: string | undefined = gqlError?.extensions?.code;
    const message: string = gqlError?.message || err?.message || 'Erro ao realizar inscrição';
    throw new InscricaoMutationError(message, codigo);
  }
};

// --------------------------------------------------------------------------------
// Buscar inscrição por ID
// --------------------------------------------------------------------------------

export const INSCRICAO_POR_ID = gql`
  query InscricaoPorId($id: ID!) {
    inscricaoTorneioPorId(id: $id) {
      id
      dataInscricao
      observacao
      status
      compartilharInformacoes
      dataCancelamento
      motivoCancelamento
      equipe {
        id
        nome
        cidade
        atletas {
          id
          usuario {
            id
            nome
            email
            username
            imagemUrl
          }
        }
      }
      categoriaTorneio {
        id
        nome
        descricao
        numeroAtletas
        status
        precoInscricao
        aplicarDescontoTorneio
        torneio {
          id
          nome
          atletaTrocaParceiro
        }
      }
      pagamentoInscricao {
        id
        status
        valor
      }
    }
  }
`;

export interface InscricaoPorIdData {
  inscricaoTorneioPorId: {
    id: string;
    dataInscricao?: string | null;
    observacao?: string | null;
    status: string;
    compartilharInformacoes: boolean;
    dataCancelamento?: string | null;
    motivoCancelamento?: string | null;
    equipe: {
      id: string;
      nome: string;
      cidade?: string | null;
      atletas: {
        id: string;
        usuario: {
          id: string;
          nome: string;
          email: string;
          username: string;
          imagemUrl?: string | null;
        };
      }[];
    };
    categoriaTorneio: {
      id: string;
      nome: string;
      descricao?: string | null;
      numeroAtletas?: number | null;
      status?: string | null;
      precoInscricao?: number | null;
      aplicarDescontoTorneio?: boolean | null;
      torneio: {
        id: string;
        nome: string;
        atletaTrocaParceiro?: boolean | null;
      };
    };
    pagamentoInscricao?: {
      id: string;
      status: string;
      valor?: number | null;
    } | null;
  };
}

export interface InscricaoPorIdVars {
  id: string;
}

export const buscarInscricaoPorId = async (id: string) => {
  const { data } = await apolloClient.query<InscricaoPorIdData, InscricaoPorIdVars>({
    query: INSCRICAO_POR_ID,
    variables: { id },
    fetchPolicy: 'no-cache',
  });
  return data?.inscricaoTorneioPorId;
};

// --------------------------------------------------------------------------------
// Trocar parceiro
// --------------------------------------------------------------------------------

const TROCAR_PARCEIRO = gql`
  mutation TrocarParceiro($input: TrocarParceiroInput!) {
    trocarParceiro(input: $input) {
      id
      nome
      atletas {
        id
        usuario {
          id
          nome
          imagemUrl
        }
      }
    }
  }
`;

export const trocarParceiro = async (
  inscricaoTorneioId: string,
  atletaSaiId: string,
  atletaEntraId: string,
) => {
  const { data } = await apolloClient.mutate<
    { trocarParceiro: { id: string } }
  >({
    mutation: TROCAR_PARCEIRO,
    variables: { input: { inscricaoTorneioId, atletaSaiId, atletaEntraId } },
  });
  if (!data?.trocarParceiro) {
    throw new Error('Erro ao trocar parceiro');
  }
  return data.trocarParceiro;
};

// --------------------------------------------------------------------------------
// Atualizar inscrição
// --------------------------------------------------------------------------------

export const ATUALIZAR_INSCRICAO_TORNEIO = gql`
  mutation AtualizarInscricaoTorneio($id: ID!, $input: InscricaoTorneioInput!) {
    atualizarInscricaoTorneio(id: $id, input: $input) {
      id
      compartilharInformacoes
      equipe {
        id
        nome
        cidade
        atletas {
          id
          usuario {
            id
            nome
            imagemUrl
            email
          }
        }
      }
      status
      observacao
      dataInscricao
      dataCancelamento
      motivoCancelamento
      categoriaTorneio {
        id
        descricao
        nome
        numeroAtletas
        precoInscricao
        aplicarDescontoTorneio
        torneio {
          id
          nome
        }
      }
      pagamentoInscricao {
        id
        status
        valor
      }
    }
  }
`;

export interface AtualizarInscricaoTorneioInput {
  categoriaTorneioId: string;
  dataInscricao?: string;
  equipe: {
    nome: string;
    cidade: string;
    atletas: { id: string }[];
  };
  status?: string;
  observacao?: string;
  pagamentoInscricaoId?: string;
}

export interface AtualizarInscricaoTorneioResponse {
  id: string;
  compartilharInformacoes: boolean;
  equipe: {
    id: string;
    nome: string;
    cidade?: string | null;
    atletas: {
      id: string;
      usuario: {
        id: string;
        nome: string;
        imagemUrl?: string | null;
        email: string;
      };
    }[];
  };
  status: string;
  observacao?: string | null;
  dataInscricao?: string | null;
  dataCancelamento?: string | null;
  motivoCancelamento?: string | null;
  categoriaTorneio: {
    id: string;
    descricao?: string | null;
    nome: string;
    numeroAtletas?: number | null;
    precoInscricao?: number | null;
    aplicarDescontoTorneio?: boolean | null;
    torneio: { id: string; nome: string };
  };
  pagamentoInscricao?: {
    id: string;
    status: string;
    valor?: number | null;
  } | null;
}

export interface AtualizarInscricaoTorneioData {
  atualizarInscricaoTorneio: AtualizarInscricaoTorneioResponse;
}

export interface AtualizarInscricaoTorneioVars {
  id: string;
  input: AtualizarInscricaoTorneioInput;
}

export const atualizarInscricaoTorneio = async (
  id: string,
  input: AtualizarInscricaoTorneioInput
) => {
  const { data } = await apolloClient.mutate<
    AtualizarInscricaoTorneioData,
    AtualizarInscricaoTorneioVars
  >({
    mutation: ATUALIZAR_INSCRICAO_TORNEIO,
    variables: { id, input },
  });
  return data?.atualizarInscricaoTorneio;
};

// --------------------------------------------------------------------------------
// Deletar inscrição
// --------------------------------------------------------------------------------

export const DELETAR_INSCRICAO_TORNEIO = gql`
  mutation DeletarInscricaoTorneio($id: ID!) {
    deletarInscricaoTorneio(id: $id)
  }
`;

export interface DeletarInscricaoTorneioData {
  deletarInscricaoTorneio: boolean;
}

export interface DeletarInscricaoTorneioVars {
  id: string;
}

export const deletarInscricaoTorneio = async (id: string) => {
  const { data } = await apolloClient.mutate<
    DeletarInscricaoTorneioData,
    DeletarInscricaoTorneioVars
  >({
    mutation: DELETAR_INSCRICAO_TORNEIO,
    variables: { id },
  });
  return data?.deletarInscricaoTorneio;
};

// --------------------------------------------------------------------------------
// Relatórios de inscrições/pagamentos (CSV)
// --------------------------------------------------------------------------------

export type RelatorioInscricoesFiltro = {
  torneioId?: string;
  categoriaTorneioId?: string;
  atletaId?: string;
  dataCriacaoInicio?: Date | string; // enviar em ISO-8601 no query param
  dataCriacaoFim?: Date | string;    // idem
  status?: string;
};

const restUrl = (process.env.REACT_APP_GRAPHQL_ENDPOINT || '') + 'api/';

export async function downloadRelatorioInscricoesCsv(
  filtros?: RelatorioInscricoesFiltro,
): Promise<void> {
  // Monta query string apenas com parâmetros definidos
  const params = new URLSearchParams();
  const add = (key: string, value?: unknown) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'string') {
      const v = value.trim();
      if (v !== '') params.append(key, v);
      return;
    }
    if (value instanceof Date) {
      params.append(key, value.toISOString());
      return;
    }
    // Para outros tipos (ex.: number/boolean) converte para string simples
    params.append(key, String(value));
  };

  add('torneioId', filtros?.torneioId);
  add('categoriaTorneioId', filtros?.categoriaTorneioId);
  add('atletaId', filtros?.atletaId);
  // Converte string de data (YYYY-MM-DD) para ISO-8601, se necessário
  const toIso = (d?: Date | string) => {
    if (!d) return undefined;
    if (d instanceof Date) return d.toISOString();
    const asStr = d.trim();
    if (!asStr) return undefined;
    // Deixa o Date normalizar e gerar ISO local->UTC
    const asDate = new Date(asStr);
    if (isNaN(asDate.getTime())) return asStr; // já pode estar em ISO-8601 válido
    return asDate.toISOString();
  };
  add('dataCriacaoInicio', toIso(filtros?.dataCriacaoInicio));
  add('dataCriacaoFim', toIso(filtros?.dataCriacaoFim));
  add('status', filtros?.status);

  const url = `${restUrl}admin/reports/inscricoes.csv${params.toString() ? `?${params.toString()}` : ''}`;

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) headers['authorization'] = `Bearer ${token}`;

  try {
    const response = await axios.get<Blob>(url, { responseType: 'blob', headers });

    // Garante o tipo CSV e dispara o download
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');

    // Nome do arquivo: relatorio_pagamentos_YYYY-MM-DD_HH-mm.csv
    const pad = (n: number) => String(n).padStart(2, '0');
    const now = new Date();
    const filename = `relatorio_pagamentos_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}.csv`;

    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  } catch (error: any) {
    if (error?.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }

    let message = 'Não foi possível baixar o relatório. Tente novamente.';
    if (error?.response?.data) {
      try {
        // Alguns backends retornam JSON com { message }
        const reader = new FileReader();
        const p = new Promise<string>((resolve) => {
          reader.onload = () => {
            try {
              const json = JSON.parse(String(reader.result));
              resolve(json?.message || message);
            } catch {
              resolve(message);
            }
          };
        });
        reader.readAsText(error.response.data);
        message = await p;
      } catch {
        // ignora e usa mensagem padrão/axios.message
        message = error?.message || message;
      }
    } else if (error?.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
}

// --------------------------------------------------------------------------------
// Consentir compartilhar informações
// --------------------------------------------------------------------------------

export const CONSENTIR_COMPARTILHAR_INFORMACOES = gql`
  mutation ConsentirCompartilharInformacoes($inscricaoTorneioId: ID!) {
    consentirCompartilharInformacoes(inscricaoTorneioId: $inscricaoTorneioId) {
      id
      compartilharInformacoes
      status
    }
  }
`;

export interface ConsentirCompartilharInformacoesData {
  consentirCompartilharInformacoes: {
    id: string;
    compartilharInformacoes: boolean;
    status: string;
  };
}

export interface ConsentirCompartilharInformacoesVars {
  inscricaoTorneioId: string;
}

export const consentirCompartilharInformacoes = async (
  inscricaoTorneioId: string
) => {
  const { data } = await apolloClient.mutate<
    ConsentirCompartilharInformacoesData,
    ConsentirCompartilharInformacoesVars
  >({
    mutation: CONSENTIR_COMPARTILHAR_INFORMACOES,
    variables: { inscricaoTorneioId },
  });
  return data?.consentirCompartilharInformacoes;
};

// ---------------------------------------------------------------------------
// inscricoesPorLigaAgrupadas — inscrições de torneios da liga agrupadas por atleta
// ---------------------------------------------------------------------------

export const INSCRICOES_POR_LIGA_AGRUPADAS = gql`
  query InscricoesPorLigaAgrupadas($ligaId: ID!) {
    inscricoesPorLigaAgrupadas(ligaId: $ligaId) {
      atletaId
      torneios {
        torneioId
        torneioNome
        categorias {
          inscricaoId
          categoriaId
          categoriaNome
          status
          dataInscricao
          equipe {
            id
            nome
          }
        }
      }
    }
  }
`;

export interface InscricaoLigaEquipe {
  id: string;
  nome: string;
}

export interface InscricaoLigaCategoria {
  inscricaoId: string;
  categoriaId: string;
  categoriaNome: string;
  status: string;
  dataInscricao?: string | null;
  equipe?: InscricaoLigaEquipe | null;
}

export interface InscricaoLigaTorneio {
  torneioId: string;
  torneioNome: string;
  categorias: InscricaoLigaCategoria[];
}

export interface InscricaoLigaAtleta {
  atletaId: string;
  torneios: InscricaoLigaTorneio[];
}

export interface InscricoesPorLigaAgrupadasData {
  inscricoesPorLigaAgrupadas: InscricaoLigaAtleta[];
}

export interface InscricoesPorLigaAgrupadasVars {
  ligaId: string;
}

export const buscarInscricoesPorLigaAgrupadas = async (
  ligaId: string
): Promise<InscricaoLigaAtleta[]> => {
  const { data } = await apolloClient.query<
    InscricoesPorLigaAgrupadasData,
    InscricoesPorLigaAgrupadasVars
  >({
    query: INSCRICOES_POR_LIGA_AGRUPADAS,
    variables: { ligaId },
    fetchPolicy: 'no-cache',
  });
  return data.inscricoesPorLigaAgrupadas;
};
